import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { PinScreen } from './PinScreen';
import { ScreenBackdrop } from './ScreenBackdrop';
import { CloseIcon } from './icons';
import { reauthApi, reauthStatus } from '../../lib/me';
import { fetchRemoteWallet, getSeed } from '../../lib/wallet';
import { ensureWalletAddresses, needsAddressBackfill } from '../../lib/addressBackfill';
import { runBiometric } from '../../lib/biometric';
import { useLockStore } from '../../store/lock';
import { useUiStore } from '../../store/ui';
import { formatCountdown } from '../../lib/lockout';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';

/** Prompt for a fresh reauth token (biometric auto-attempt + PIN fallback).
 *  Resolves with the token via onSuccess; onCancel backs out. A brute-force
 *  lockout (too many wrong PINs) blocks ONLY this reauth — shown as a dismissible
 *  countdown here — never the whole app, since the PIN just gates sensitive
 *  actions (transfers, card ops, seed), not app entry. */
export function ReauthFlow({
  initData,
  biometricEnabled,
  reason,
  onSuccess,
  onCancel,
}: {
  initData: string;
  biometricEnabled: boolean;
  reason?: string;
  onSuccess: (token: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [attempts, setAttempts] = useState(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [checking, setChecking] = useState(true);
  // Token exchange after Face ID / fingerprint — PIN pad stays mounted underneath
  // otherwise it looks frozen for the round-trip.
  const [exchanging, setExchanging] = useState(false);
  const lock = useLockStore((s) => s.lock);
  const lockedUntil = useLockStore((s) => s.lockedUntil);
  const [now, setNow] = useState(() => Date.now());
  const isLocked = lockedUntil > now;
  const tried = useRef(false);
  const openSheet = useUiStore((s) => s.openSheet);
  const closeSheet = useUiStore((s) => s.closeSheet);
  const qc = useQueryClient();

  // Every successful reauth is a moment the seed is reachable for PIN users —
  // ride it to silently backfill missing BTC addresses (wallets registered
  // before Bitcoin support). Fire-and-forget: never delays the caller.
  const succeed = (token: string) => {
    void (async () => {
      try {
        const wallet = await fetchRemoteWallet(initData);
        if (!needsAddressBackfill(wallet)) return;
        const m = await getSeed(initData, token);
        if (m) {
          await ensureWalletAddresses(initData, m, wallet, () =>
            qc.invalidateQueries({ queryKey: ['wallet'] }),
          );
        }
      } catch {
        /* silent — next reauth/send retries */
      }
    })();
    onSuccess(token);
  };

  // Hide the bottom nav for the whole reauth lifetime. Callers that close their
  // BottomSheet when opening us would otherwise drop sheetCount to 0 and the nav
  // would paint above this overlay (it's a WalletScreen sibling of the tab page).
  // useLayoutEffect: bump before paint so we don't flash the nav for a frame when
  // a sheet closes in the same commit we mount.
  useLayoutEffect(() => {
    openSheet();
    return () => closeSheet();
  }, [openSheet, closeSheet]);

  useEffect(() => {
    let alive = true;
    void reauthStatus(initData)
      .then((s) => {
        if (alive && s.lockedUntil > Date.now()) lock(s.lockedUntil);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setChecking(false);
      });
    return () => {
      alive = false;
    };
  }, [initData, lock]);

  // Tick while locked so the countdown updates and the PIN screen returns when it
  // elapses.
  useEffect(() => {
    if (!isLocked) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isLocked]);

  useEffect(() => {
    if (checking || isLocked || !biometricEnabled || tried.current) return;
    tried.current = true;
    void (async () => {
      const ok = await runBiometric(reason ?? t('reauth.reason'));
      if (!ok) return;
      setExchanging(true);
      try {
        const r = await reauthApi(initData, { method: 'biometric' });
        if (r.ok) succeed(r.token);
        else if (r.lockedUntil) lock(r.lockedUntil);
      } finally {
        setExchanging(false);
      }
    })();
  }, [checking, isLocked, biometricEnabled, initData, reason, onSuccess, t, lock]);

  const onPin = async (pin: string) => {
    const r = await reauthApi(initData, { method: 'pin', pin });
    if (r.ok) {
      succeed(r.token);
      return;
    }
    if (r.lockedUntil) {
      lock(r.lockedUntil);
      return;
    }
    setRemaining(r.remaining ?? null);
    setAttempts((a) => a + 1);
  };

  if (checking || exchanging) {
    return (
      <div className="relative flex h-full min-h-full flex-col">
        <ScreenBackdrop full />
        <div className="relative z-10 flex h-full min-h-full items-center justify-center">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-ink" />
        </div>
      </div>
    );
  }

  // Locked out — show a dismissible countdown (the user can back out and keep
  // using the app; only this action is blocked until the lock elapses).
  if (isLocked) {
    return <LockedReauth until={lockedUntil} now={now} onCancel={onCancel} />;
  }

  const title =
    attempts > 0
      ? remaining != null
        ? t('reauth.wrongRemaining', { count: remaining })
        : t('reauth.wrong')
      : t('reauth.title');

  return (
    <PinScreen key={attempts} error={attempts > 0} title={title} onComplete={onPin} onClose={onCancel} />
  );
}

/** Dismissible lockout countdown shown in place of the PIN pad. */
function LockedReauth({ until, now, onCancel }: { until: number; now: number; onCancel: () => void }) {
  const { t } = useTranslation();
  useBackButton(onCancel);
  const tgBack = hasTelegramBackButton();
  return (
    <div className="relative flex h-full min-h-full flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex h-full min-h-full flex-col px-4 pb-[max(1.25rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        {!tgBack && (
          <div className="flex h-10 items-center">
            <button
              onClick={onCancel}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.35] text-white"
              aria-label="Close"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        )}
        <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
          <h1 className="text-[20px] font-semibold text-[#ff3b30]">{t('lock.blocked')}</h1>
          <p className="mt-3 text-[15px] tabular-nums text-muted">
            {t('lock.blockedIn', { time: formatCountdown(until - now) })}
          </p>
        </div>
      </div>
    </div>
  );
}
